/**
 * Recover from stale-deploy chunk failures.
 *
 * After a new build is deployed, a tab still running the old `main.*.js`
 * asks for lazy chunks whose hashed files no longer exist. The server's SPA
 * fallback answers with index.html, so the browser reports
 * "Unexpected token '<'" and webpack rejects with a ChunkLoadError.
 * Reloading the page picks up the new index.html and its matching chunks.
 *
 * A sessionStorage timestamp stops a reload loop when the chunk is missing
 * from the current build too (a broken deploy): we reload at most once per
 * RELOAD_COOLDOWN_MS.
 */

const STORAGE_KEY = 'chunk_load_error_reloaded_at';
const RELOAD_COOLDOWN_MS = 10000;

const isChunkLoadError = error => {
  if (!error) return false;
  const name = error.name || '';
  const message = String(error.message || error);
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

const readLastReload = () => {
  try {
    return Number(sessionStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeLastReload = value => {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(value));
  } catch {}
};

const reloadOnce = () => {
  const now = Date.now();
  if (now - readLastReload() < RELOAD_COOLDOWN_MS) return false;
  writeLastReload(now);
  window.location.reload();
  return true;
};

const handleError = (error, event) => {
  if (!isChunkLoadError(error)) return;
  if (reloadOnce() && event?.preventDefault) event.preventDefault();
};

export const registerChunkLoadRecovery = () => {
  if (typeof window === 'undefined') return;

  // Errors thrown during render (React.lazy) reach here via reportError.
  window.addEventListener('error', event => handleError(event.error, event));

  // Imperative `import()` calls that nobody catches.
  window.addEventListener('unhandledrejection', event => handleError(event.reason, event));
};

export default registerChunkLoadRecovery;
